/**
 * Chamadas da Fase 6 (relatórios): sincronizar insights, links públicos, agendamentos,
 * relatório público (senha no corpo) e downloads (PDF, CSV, XLSX) que não cabem em `queries`.
 */
import type {
  CreateReportLinkInput,
  CreateReportScheduleInput,
  InsightsSyncStatusDto,
  PeriodQuery,
  PublicReportDataDto,
  ReportLinkDto,
  ReportScheduleDto,
  ReportScheduleRunDto,
  UpdateReportScheduleInput,
  WorkspaceDto,
} from "@iagenix/shared";
import { api, ApiRequestError } from "./api";

export function reportsKey(workspaceId: string) {
  return ["workspaces", workspaceId, "reports"];
}

/** Query string do período (preset ou intervalo). */
export function periodParams(period: PeriodQuery): Record<string, string> {
  const p: Record<string, string> = {};
  if (period.preset) p.preset = period.preset;
  else {
    if (period.from) p.from = period.from;
    if (period.to) p.to = period.to;
  }
  return p;
}

async function readError(res: Response): Promise<ApiRequestError> {
  const data = (await res.json().catch(() => null)) as {
    error?: { code?: string; message?: string; details?: unknown };
  } | null;
  return new ApiRequestError(
    res.status,
    data?.error?.code ?? "erro_http",
    data?.error?.message ?? `O servidor respondeu com erro ${res.status}.`,
    data?.error?.details,
  );
}

/** Baixa um arquivo (GET com cookie, ou POST com corpo) e dispara o download no navegador. */
export async function downloadFile(
  url: string,
  init: { method?: "GET" | "POST"; body?: unknown } = {},
): Promise<string> {
  const res = await fetch(url, {
    method: init.method ?? "GET",
    credentials: "same-origin",
    headers: init.body !== undefined ? { "Content-Type": "application/json" } : {},
    body: init.body !== undefined ? JSON.stringify(init.body) : null,
  });
  if (!res.ok) throw await readError(res);
  const blob = await res.blob();
  const disposition = res.headers.get("content-disposition") ?? "";
  const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? "arquivo";
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 10_000);
  return filename;
}

export const reportsApi = {
  syncInsights: (workspaceId: string, account: string) =>
    api<InsightsSyncStatusDto & { debounced: boolean }>(
      `/api/workspaces/${workspaceId}/reports/accounts/${account}/sync`,
      { method: "POST" },
    ),
  createLink: (workspaceId: string, body: CreateReportLinkInput) =>
    api<ReportLinkDto>(`/api/workspaces/${workspaceId}/reports/links`, { method: "POST", body }),
  revokeLink: (workspaceId: string, linkId: string) =>
    api<undefined>(`/api/workspaces/${workspaceId}/reports/links/${linkId}`, { method: "DELETE" }),
  createSchedule: (workspaceId: string, body: CreateReportScheduleInput) =>
    api<ReportScheduleDto>(`/api/workspaces/${workspaceId}/reports/schedules`, {
      method: "POST",
      body,
    }),
  updateSchedule: (workspaceId: string, id: string, body: UpdateReportScheduleInput) =>
    api<ReportScheduleDto>(`/api/workspaces/${workspaceId}/reports/schedules/${id}`, {
      method: "PATCH",
      body,
    }),
  deleteSchedule: (workspaceId: string, id: string) =>
    api<undefined>(`/api/workspaces/${workspaceId}/reports/schedules/${id}`, { method: "DELETE" }),
  runSchedule: (workspaceId: string, id: string) =>
    api<ReportScheduleRunDto>(`/api/workspaces/${workspaceId}/reports/schedules/${id}/run`, {
      method: "POST",
    }),
  unlockPublic: (token: string, body: { password?: string } & PeriodQuery) =>
    api<PublicReportDataDto>(`/api/public/reports/${token}/data`, { method: "POST", body }),
  downloadPublicPdf: (token: string, body: { password?: string } & PeriodQuery) =>
    downloadFile(`/api/public/reports/${token}/pdf`, { method: "POST", body }),
  downloadPdf: (workspaceId: string, params: Record<string, string>) =>
    downloadFile(
      `/api/workspaces/${workspaceId}/reports/dashboard.pdf?${new URLSearchParams(params).toString()}`,
    ),
  downloadExport: (workspaceId: string, params: Record<string, string>) =>
    downloadFile(
      `/api/workspaces/${workspaceId}/reports/export?${new URLSearchParams(params).toString()}`,
    ),
  async uploadLogo(workspaceId: string, file: File): Promise<WorkspaceDto> {
    const form = new FormData();
    form.set("file", file, file.name);
    const res = await fetch(`/api/workspaces/${workspaceId}/logo`, {
      method: "PUT",
      body: form,
      credentials: "same-origin",
    });
    if (!res.ok) throw await readError(res);
    return (await res.json()) as WorkspaceDto;
  },
  removeLogo: (workspaceId: string) =>
    api<WorkspaceDto>(`/api/workspaces/${workspaceId}/logo`, { method: "DELETE" }),
};
